/**
 * React Query Provider
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactNode, useState } from 'react'
import { captureException } from '@/lib/posthog'
import { supabase } from '@/lib/supabase'
import { installSessionAwareFocus } from '@/lib/session-aware-focus'

// Refetch-on-focus waits for an expired session to refresh first (#1461), so a
// resumed tab or app doesn't send a burst of requests with a stale token.
installSessionAwareFocus(supabase.auth)

interface QueryProviderProps {
  children: ReactNode
}

/**
 * Decide whether a failed mutation should be retried.
 *
 * Mutation retries exist for transient failures (offline / iOS PWA cold-start,
 * which surface as a network error with no response). A definite server response
 * never benefits from a retry and must NOT be repeated — e.g. a deactivation
 * guard raising `Cannot deactivate … linked to an active practice` would
 * otherwise fire the same write 3 times and delay the error toast by ~3s.
 *
 * A Supabase `PostgrestError` always carries a `code`, and any 4xx is a client
 * error — both mean "the server answered, don't retry". Everything else
 * (network/transport failures with no code) retries up to twice.
 */
export function shouldRetryMutation(failureCount: number, error: unknown): boolean {
  const code = (error as { code?: unknown } | null)?.code
  const status = (error as { status?: unknown } | null)?.status
  const isServerResponse =
    (typeof code === 'string' && code.length > 0) ||
    (typeof status === 'number' && status >= 400 && status < 500)
  if (isServerResponse) return false
  return failureCount < 2
}

export function QueryProvider({ children }: QueryProviderProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Stale time: 5 minutes
            staleTime: 5 * 60 * 1000,
            // Cache time: 30 minutes
            gcTime: 30 * 60 * 1000,
            // Retry failed requests up to 3 times with backoff
            retry: 3,
            retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
            // Always run queries regardless of navigator.onLine — avoids iOS cold-start
            // where WKWebView briefly reports offline, causing queries to be paused
            networkMode: 'always',
            // Refetch on window focus — important for iOS WKWebView to unstick stale state
            refetchOnWindowFocus: true,
          },
          mutations: {
            // Retry transient (offline / cold-start) failures up to 2×, but never
            // retry a definite server response (e.g. a guard rejection) — see
            // shouldRetryMutation.
            retry: shouldRetryMutation,
            // Exponential backoff: 1s, 2s — gives service worker time to activate
            retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 4000),
            onError: (error) => {
              if (import.meta.env.DEV) {
                console.error('Mutation error:', error)
              }
              const err = error instanceof Error ? error : new Error(String(error))
              captureException(err, {
                mutation_error: true,
                is_pwa: window.matchMedia('(display-mode: standalone)').matches,
                is_ios: /iPad|iPhone|iPod/.test(navigator.userAgent),
                user_agent: navigator.userAgent,
                online: navigator.onLine,
                url: window.location.href,
              })
            },
          },
        },
      })
  )

  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  )
}
